/**
 * How the editor names a live scene object across a command boundary.
 *
 * three offers two names: the numeric `object.id` and the `uuid`. The numeric one
 * is a PER-INSTANCE counter, so with the page's three and the editor's own bundle
 * both loaded (chrome extension, editor-injected helpers) the same number names two
 * different objects. The uuid is therefore the handle; the numeric id is kept only
 * as a fallback for callers that have not been migrated, and resolution reports
 * which rung matched so that fallback can be flagged rather than silently taken.
 *
 * The bug this closes: a hierarchy node id reached `findObjectById` — which matches
 * numeric ids too — and resolved to a different object ("property at path angle not
 * found on object with ID 18"). One mint point means a caller cannot pick the wrong
 * name by accident.
 */

/** duck-typed: a three object, a hierarchy node, anything that names itself */
export interface HandleTarget {
    readonly id?: unknown;
    readonly uuid?: unknown;
}

/** The handle for an object — its uuid, or the numeric id when it has none. */
export function handleOf(target: HandleTarget | null | undefined): string | null {
    if (!target) return null;
    if (typeof target.uuid === "string" && target.uuid.length > 0) return target.uuid;
    if (typeof target.id === "string" && target.id.length > 0) return target.id;
    if (typeof target.id === "number") return String(target.id);
    return null;
}

interface GraphObject extends HandleTarget {
    readonly children?: readonly unknown[];
}

export type HandleRung = "uuid" | "id";

/**
 * Find the object a handle names, and say which rung matched.
 *
 * The uuid rung wins across the WHOLE tree before any numeric match is accepted —
 * a depth-first walk that tested both per node would return whichever object it
 * met first, which is exactly the collision this is meant to avoid.
 */
export function resolveObjectHandle(
    root: GraphObject | null | undefined,
    handle: string,
): { object: object; rung: HandleRung } | null {
    if (!root || !handle) return null;
    const byUuid = walk(root, node => node.uuid === handle);
    if (byUuid) return { object: byUuid, rung: "uuid" };
    const byId = walk(root, node => String(node.id) === handle);
    return byId ? { object: byId, rung: "id" } : null;
}

/** the slice of the resource registry this needs — no service import, so the
 *  handle module stays a leaf */
interface ResourceRegistryLike {
    entries(): Iterable<{ uuid?: string; deref(): object | undefined }>;
}

/**
 * The resource a handle names — a material, texture or geometry.
 *
 * Resources carry the same kind of handle (their uuid) but do NOT live in the
 * scene graph, so {@link resolveObjectHandle} cannot see them. Without this an
 * address handed out by the resources list resolves to nothing, and the only way
 * to act on a material is to already have its owning mesh.
 */
export function resolveResourceHandle(resources: ResourceRegistryLike, handle: string): object | null {
    if (!handle) return null;
    for (const entry of resources.entries()) {
        if (entry.uuid !== handle) continue;
        // an entry can outlive its resource — the registry is weak
        return entry.deref() ?? null;
    }
    return null;
}

/**
 * Resolve a handle the way the engine does: every scene graph first, then the
 * resource registry.
 *
 * BOTH rungs matter to one caller — the addresses `resources_list` hands out name
 * materials and textures, which are in no scene at all, while everything from the
 * hierarchy is. Kept here rather than inline in the engine so the order can be
 * tested without standing up a renderer.
 *
 * Scenes are searched one at a time, so a numeric match in an earlier scene beats
 * a uuid match in a later one — the pre-existing order, not a decision made here.
 */
export function resolveHandleInGraphs(
    roots: Iterable<GraphObject | null | undefined>,
    resources: ResourceRegistryLike | null | undefined,
    handle: string,
): { object: object; rung: HandleRung | "resource" } | null {
    for (const root of roots) {
        if (!root) continue;
        const found = resolveObjectHandle(root, handle);
        if (found) return found;
    }
    if (!resources) return null;
    const resource = resolveResourceHandle(resources, handle);
    return resource ? { object: resource, rung: "resource" } : null;
}

function walk(node: GraphObject, matches: (node: GraphObject) => boolean): object | null {
    if (matches(node)) return node as object;
    for (const child of node.children ?? []) {
        const found = walk(child as GraphObject, matches);
        if (found) return found;
    }
    return null;
}
